import axios from 'axios';
import { call, put, takeEvery } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { api } from 'services/api';
import { notificationContainer } from 'utils/notificationContainer';
import {
	assignScriptFailure,
	assignScriptRequest,
	assignScriptSuccess,
	getAssignedScriptsFailure,
	getAssignedScriptsRequest,
	getAssignedScriptsSuccess,
	getAvailableScriptsFailure,
	getAvailableScriptsRequest,
	getAvailableScriptsSuccess,
	removeScriptFailure,
	removeScriptRequest,
	removeScriptSuccess,
} from './reducer';
import {
	TEmAutomationAssignedScriptsRequest,
	TEmAutomationAssignedScriptsResponsePayload,
	TEmAutomationAssignScriptRequest,
	TEmAutomationAvailableScriptsResponsePayload,
	TEmAutomationRemoveScriptRequest,
} from './types';

function* getAvailableScriptsRequestWorker() {
	try {
		const response: TEmAutomationAvailableScriptsResponsePayload = yield call(
			api.emAutomationScripts.getAvailableScripts,
		);
		yield put(getAvailableScriptsSuccess(response));
	} catch (error) {
		yield put(getAvailableScriptsFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error.response?.data?.message ?? 'Error'), 'error');
		}
	}
}

function* getAssignedScriptsRequestWorker({
	payload,
}: PayloadAction<TEmAutomationAssignedScriptsRequest>) {
	try {
		const response: TEmAutomationAssignedScriptsResponsePayload = yield call(
			api.emAutomationScripts.getAssignedScripts,
			payload,
		);
		yield put(getAssignedScriptsSuccess(response));
	} catch (error) {
		yield put(getAssignedScriptsFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error.response?.data?.message ?? 'Error'), 'error');
		}
	}
}

function* assignScriptRequestWorker({ payload }: PayloadAction<TEmAutomationAssignScriptRequest>) {
	try {
		yield call(api.emAutomationScripts.assignScript, payload.data);
		yield put(assignScriptSuccess());
		notificationContainer('Script assigned successfully', 'success');
		if (payload.getAssignedScriptsRequest) {
			yield put(getAssignedScriptsRequest(payload.getAssignedScriptsRequest));
		}
	} catch (error) {
		yield put(assignScriptFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error.response?.data?.message ?? 'Error'), 'error');
		}
	}
}

function* removeScriptRequestWorker({ payload }: PayloadAction<TEmAutomationRemoveScriptRequest>) {
	try {
		yield call(api.emAutomationScripts.removeScript, payload);
		yield put(removeScriptSuccess());
		notificationContainer('Script removed successfully', 'success');
		if (payload.getAssignedScriptsRequest) {
			yield put(getAssignedScriptsRequest(payload.getAssignedScriptsRequest));
		}
	} catch (error) {
		yield put(removeScriptFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error.response?.data?.message ?? 'Error'), 'error');
		}
	}
}

export function* emAutomationScriptsSaga() {
	yield takeEvery(getAvailableScriptsRequest.type, getAvailableScriptsRequestWorker);
	yield takeEvery(getAssignedScriptsRequest.type, getAssignedScriptsRequestWorker);
	yield takeEvery(assignScriptRequest.type, assignScriptRequestWorker);
	yield takeEvery(removeScriptRequest.type, removeScriptRequestWorker);
}
